import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SeoHead from "@/components/feature/SeoHead";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

function formatNumber(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return n.toString();
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<"latest" | "hot">("latest");

  const category = categories.find((c) => c.slug === slug);

  const filteredArticles = useMemo(() => {
    let filtered = articles.filter((a) => a.category === slug);
    if (sortBy === "hot") {
      filtered = [...filtered].sort((a, b) => b.viewCount - a.viewCount);
    } else {
      filtered = [...filtered].sort(
        (a, b) =>
          new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
      );
    }
    return filtered;
  }, [slug, sortBy]);

  const allCategoryArticles = useMemo(() => {
    return [...articles].sort(
      (a, b) =>
        new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
    );
  }, []);

  const displayArticles = category ? filteredArticles : allCategoryArticles;

  const catName = category?.name || "全部爆料";
  const catDesc =
    category?.description || "黑料在线精选全网热门爆料，涵盖娱乐八卦、网红爆料、社会热点等多个频道。";

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: catName },
  ];

  const webpageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${catName} - 黑料在线`,
    url: `http://lvepkfa.cn/category/${slug || "all"}`,
    description: catDesc,
    isPartOf: {
      "@type": "WebSite",
      name: "黑料在线",
      url: "http://lvepkfa.cn/",
    },
    about: category
      ? {
          "@type": "Thing",
          name: category.name,
        }
      : undefined,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "http://lvepkfa.cn/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: catName,
        item: `http://lvepkfa.cn/category/${slug || "all"}`,
      },
    ],
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: displayArticles.slice(0, 10).map((article, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `http://lvepkfa.cn/article/${article.id}`,
      name: article.title,
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHead
        title={`${catName} - 黑料在线 | 每日黑料吃瓜网精选爆料`}
        description={catDesc}
        keywords={`${catName},黑料在线,黑料视频在线观看,每日黑料吃瓜网,国产在线黑料,在线看黑料`}
        canonicalUrl={`http://lvepkfa.cn/category/${slug || "all"}`}
        ogType="website"
        schema={[webpageSchema, breadcrumbSchema, itemListSchema]}
      />

      <Navbar />

      <main className="pt-16 md:pt-20">
        {/* Header Banner */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-10 max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mt-3 mb-2">
              {catName}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
              {catDesc}
            </p>
          </div>
        </div>

        <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Filter Bar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5 md:mb-6">
                <p className="text-sm text-foreground-500">
                  共 <span className="font-semibold text-foreground-700">{displayArticles.length}</span> 篇爆料
                </p>
                <div className="flex items-center gap-1 p-1 bg-background-100 rounded-full">
                  <button
                    onClick={() => setSortBy("latest")}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === "latest"
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-500 hover:text-foreground-700"
                    }`}
                  >
                    最新发布
                  </button>
                  <button
                    onClick={() => setSortBy("hot")}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === "hot"
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-500 hover:text-foreground-700"
                    }`}
                  >
                    最热阅读
                  </button>
                </div>
              </div>

              {/* Articles Grid */}
              {displayArticles.length > 0 ? (
                <div className="space-y-4 md:space-y-5">
                  {displayArticles.map((article) => (
                    <article
                      key={article.id}
                      className="flex flex-col sm:flex-row gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors group border border-background-200/40"
                      itemScope
                      itemType="https://schema.org/Article"
                    >
                      <Link
                        to={`/article/${article.id}`}
                        className="block w-full sm:w-44 md:w-52 flex-shrink-0 rounded-lg overflow-hidden aspect-[16/10] cursor-pointer"
                      >
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          width={400}
                          height={250}
                          itemProp="image"
                        />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Link
                            to={`/category/${article.category}`}
                            className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap"
                          >
                            {article.categoryName}
                          </Link>
                          <span className="text-xs text-foreground-400">
                            {article.publishDate}
                          </span>
                          <span className="text-xs text-foreground-400 flex items-center gap-1">
                            <i className="ri-eye-line" />
                            {formatNumber(article.viewCount)}
                          </span>
                        </div>
                        <h2 className="text-base md:text-lg font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          <Link
                            to={`/article/${article.id}`}
                            itemProp="headline"
                            className="cursor-pointer"
                          >
                            {article.title}
                          </Link>
                        </h2>
                        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
                          {article.summary}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="text-xs px-2 py-0.5 rounded-full bg-background-200 text-foreground-500"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-background-100 rounded-xl">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400" />
                  </div>
                  <p className="text-foreground-500 mb-2">该分类暂无爆料内容</p>
                  <Link
                    to="/"
                    className="text-sm text-primary-500 hover:text-primary-600 font-medium"
                  >
                    返回首页查看更多
                  </Link>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Categories */}
              <div className="p-4 rounded-xl bg-background-100 border border-background-200/40">
                <h3 className="text-base font-bold text-foreground-950 mb-4">
                  全部分类
                </h3>
                <div className="space-y-2">
                  <Link
                    to="/"
                    className={`flex items-center justify-between p-2.5 rounded-lg transition-colors ${
                      !category
                        ? "bg-primary-100 text-primary-700"
                        : "hover:bg-background-200 text-foreground-600"
                    }`}
                  >
                    <span className="text-sm font-medium">全部爆料</span>
                    <span className="text-xs">{articles.length}篇</span>
                  </Link>
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center justify-between p-2.5 rounded-lg transition-colors ${
                        category?.id === cat.id
                          ? "bg-primary-100 text-primary-700"
                          : "hover:bg-background-200 text-foreground-600"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <i className={`${cat.icon} text-xs`} />
                        <span className="text-sm font-medium">{cat.name}</span>
                      </div>
                      <span className="text-xs">{cat.articleCount}篇</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Hot Articles */}
              <div className="p-4 rounded-xl bg-background-100 border border-background-200/40">
                <h3 className="text-base font-bold text-foreground-950 mb-4">
                  本周最热
                </h3>
                <div className="space-y-3">
                  {[...articles]
                    .sort((a, b) => b.viewCount - a.viewCount)
                    .slice(0, 5)
                    .map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <span className="flex-shrink-0 w-5 h-5 rounded text-xs font-bold flex items-center justify-center bg-background-200 text-foreground-500">
                          {index + 1}
                        </span>
                        <p className="text-sm text-foreground-600 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {article.title}
                        </p>
                      </Link>
                    ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
